import { Eye } from "lucide-react";
import type { ReactNode } from "react";

import Button from "../../common/Button/Button";
import Field from "../Field/Field";
import Select, { type SelectOption } from "../Select/Select";

export interface ExistingResourceSelectProps {
  disabled?: boolean;
  onSelectionChange: (resourceId?: string) => void;
  /** Opens the selected resource. When set, a View button is shown at the end of the select while one is selected. */
  onView?: (resourceId: string) => void;
  options: SelectOption[];
  resourceLabel: string;
  selectedId?: string;
  /** Replaces the field label, which defaults to the capitalised `resourceLabel`. */
  label?: ReactNode;
  /** Shown between the label and the select. */
  description?: ReactNode;
  /** Replaces the default "Select an existing …" placeholder. */
  placeholder?: string;
  /** Shown below the select. */
  footer?: ReactNode;
  /** The field's name in the surrounding form context (e.g. for its error messages); it isn't rendered to the DOM. */
  name?: string;
}

export default function ExistingResourceSelect({
  disabled = false,
  onSelectionChange,
  onView,
  options,
  resourceLabel,
  selectedId,
  label,
  description,
  placeholder,
  footer,
  name,
}: ExistingResourceSelectProps) {
  const selectedExists = Boolean(selectedId) && options.some((option) => option.value === selectedId);
  const selectableOptions = selectedId && !selectedExists
    ? [{ label: `Missing ${resourceLabel} (${selectedId})`, value: selectedId }, ...options]
    : options;

  return <Field fullWidth name={name}>
    <Field.Label>{label ?? resourceLabel[0].toUpperCase() + resourceLabel.slice(1)}</Field.Label>
    {description ? <div className="mb-2 text-sm text-foreground">{description}</div> : null}
    <Field.Control>
      <Select
        clearable={Boolean(selectedId)}
        disabled={disabled}
        options={selectableOptions}
        placeholder={placeholder ?? `Select an existing ${resourceLabel}`}
        value={selectedId || null}
        onValueChange={(value) => onSelectionChange(value || undefined)}
        endAdornment={onView && selectedId && selectedExists ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            label={`View ${resourceLabel}`}
            onClick={() => onView(selectedId)}
          >
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" />View</span>
          </Button>
        ) : undefined}
      />
    </Field.Control>
    {footer ? <div className="mt-1 text-xs text-muted-foreground">{footer}</div> : null}
  </Field>;
}
